import {
  BadgeIcon,
  BarChartIcon,
  BorderAllIcon,
  CounterClockwiseClockIcon,
  CrossCircledIcon,
  Crosshair2Icon,
  MagnifyingGlassIcon,
  MixerHorizontalIcon,
  StopwatchIcon,
} from '@radix-ui/react-icons';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { Box, Flex, Grid, Heading, Skeleton, Text } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import {
  BaseballIcon,
  LogFWIcon,
  LogServiceIcon,
  MachineIcon,
  R2FIcon,
  ShotHistoryIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { PlottingTab } from 'components/sections/admin-portal/machines/dialogs/performance/plotting.tab';
import { PitchPerformanceHoC } from 'components/sections/admin-portal/machines/dialogs/pitch-performance';
import { MachineActions } from 'components/sections/admin-portal/machines/dialogs/settings/machine-actions';
import { MachineBurnIn } from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in';
import { MachineErrors } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors';
import { MachineHardwareDiagnostics } from 'components/sections/admin-portal/machines/dialogs/settings/machine-hardware-diagnostics';
import { MachineInspect } from 'components/sections/admin-portal/machines/dialogs/settings/machine-inspect';
import { MachineLiveFires } from 'components/sections/admin-portal/machines/dialogs/settings/machine-live-fires';
import { MachineLogs } from 'components/sections/admin-portal/machines/dialogs/settings/machine-logs';
import { MachineSessionHistory } from 'components/sections/admin-portal/machines/dialogs/settings/machine-session-history';
import { MachineShotHistory } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history';
import { MachineStatus } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status';
import { TrainingPerformance } from 'components/sections/admin-portal/machines/dialogs/training-performance';
import { MachineEditor } from 'components/sections/admin-portal/machines/editor';
import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { MachineServiceLogs } from 'components/sections/admin-portal/machines/service-logs';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MACHINE_SETTINGS_CONTENT_ID } from 'enums/admin-portal';
import { MachineSettingsSection } from 'enums/machine-settings';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { IMachineSettingsSectionDef } from 'interfaces/i-machine-settings';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IWSMsg } from 'lib_ts/interfaces/i-machine-msg';
import { IForwardWsRequest } from 'lib_ts/interfaces/machine-msg/i-forward-ws';
import { useEffect, useMemo } from 'react';
import { getSocket } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineSettingsDialog';

const SectionContent = () => {
  const { activeSection } = useMachineSettingsDialogState();
  const machine = useMachinesPortalStore((s) => s.machine);

  if (!machine) {
    return null;
  }

  switch (activeSection) {
    case MachineSettingsSection.Status: {
      return <MachineStatus />;
    }
    case MachineSettingsSection.Configuration: {
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          flexGrow="1"
          overflow="hidden"
        >
          <Heading size="4">{t('admin.configuration')}</Heading>
          <MachineEditor />
        </Flex>
      );
    }
    case MachineSettingsSection.Errors: {
      // heading provided by MachineErrors component since it has some internal navigation state
      return <MachineErrors />;
    }
    case MachineSettingsSection.Inspect: {
      return (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Heading size="4">{t('main.inspect')}</Heading>
          <MachineInspect />
        </Flex>
      );
    }
    case MachineSettingsSection.PitchReport: {
      return (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size="4">{t('admin.pitch-report')}</Heading>
            <SuperAdminIcon />
          </Flex>
          <PitchPerformanceHoC machine={machine} />
        </Flex>
      );
    }
    case MachineSettingsSection.TrainingPerformance: {
      return <TrainingPerformance machine={machine} />;
    }
    case MachineSettingsSection.Plotting: {
      return (
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size="4">{t('admin.plotting')}</Heading>
            <SuperAdminIcon />
          </Flex>
          <PlottingTab machine={machine} />
        </Flex>
      );
    }
    case MachineSettingsSection.ServiceLogs: {
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size="4">{t('common.service-logs')}</Heading>
            <SuperAdminIcon />
          </Flex>
          <MachineServiceLogs machine={machine} />
        </Flex>
      );
    }
    case MachineSettingsSection.FWLogs: {
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size="4">{t('common.firmware-logs')}</Heading>
            <SuperAdminIcon />
          </Flex>
          <MachineLogs />
        </Flex>
      );
    }
    case MachineSettingsSection.HwDiagnostics: {
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <MachineHardwareDiagnostics />
        </Flex>
      );
    }
    case MachineSettingsSection.SessionHistory: {
      // heading provided by MachineSessionHistory component since it has some internal navigation state
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <MachineSessionHistory />
        </Flex>
      );
    }
    case MachineSettingsSection.ShotHistory: {
      // heading provided by MachineShotHistory component since it has some internal navigation state
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <MachineShotHistory />
        </Flex>
      );
    }
    case MachineSettingsSection.LiveFires: {
      // heading provided by MachineLiveFires component since it has some internal navigation state
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <MachineLiveFires />
        </Flex>
      );
    }
    case MachineSettingsSection.BurnInTests: {
      // heading provided by MachineLiveFires component since it has some internal navigation state
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={RADIX.VFLEX.GROW}
        >
          <MachineBurnIn />
        </Flex>
      );
    }
    case MachineSettingsSection.Actions: {
      return (
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.MD}
          className={RADIX.VFLEX.GROW}
        >
          <Heading size="4">{t('common.actions')}</Heading>
          <MachineActions />
        </Flex>
      );
    }
    default: {
      return null;
    }
  }
};

interface IMachineSettingsDialogProps {
  onClose: () => void;
}

export const MachineSettingsDialog = ({
  onClose,
}: IMachineSettingsDialogProps) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { activeSection, setActiveSection } = useMachineSettingsDialogState({
    // tell the hook to load & poll for machine details
    loadMachineDetails: true,
  });

  const machine = useMachinesPortalStore((s) => s.machine);

  const visibleSections = useMemo(() => {
    const ALL_MACHINE_SETTINGS_SECTIONS: IMachineSettingsSectionDef[] = [
      {
        id: 'status',
        label: t('admin.status'),
        value: MachineSettingsSection.Status,
        prefixIcon: <R2FIcon />,
      },
      {
        id: 'configuration',
        label: t('admin.configuration'),
        value: MachineSettingsSection.Configuration,
        prefixIcon: <MixerHorizontalIcon />,
      },
      {
        id: 'errors',
        label: t('common.errors'),
        value: MachineSettingsSection.Errors,
        prefixIcon: <CrossCircledIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.sections.adminErrorTypes,
      },
      {
        id: 'inspect',
        label: t('main.inspect'),
        value: MachineSettingsSection.Inspect,
        prefixIcon: <MagnifyingGlassIcon />,
      },
      {
        id: 'pitch-report',
        label: 'Pitch Report',
        value: MachineSettingsSection.PitchReport,
        prefixIcon: <BaseballIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.pitchReport,
      },
      {
        id: 'training-performance',
        label: 'Training Performance',
        value: MachineSettingsSection.TrainingPerformance,
        prefixIcon: <Crosshair2Icon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.pitchReport,
      },
      {
        id: 'plotting',
        label: t('admin.plotting'),
        value: MachineSettingsSection.Plotting,
        prefixIcon: <BorderAllIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.plotting,
      },
      {
        id: 'service-logs',
        label: 'Service Logs',
        value: MachineSettingsSection.ServiceLogs,
        prefixIcon: <LogServiceIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.serviceLogs,
      },
      {
        id: 'fw-logs',
        label: 'FW Logs',
        value: MachineSettingsSection.FWLogs,
        prefixIcon: <LogFWIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.fwLogs,
      },
      {
        id: 'hw-diagnostics',
        label: 'HW Diagnostics',
        value: MachineSettingsSection.HwDiagnostics,
        prefixIcon: <MachineIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.hwDiagnostics,
      },
      {
        id: 'shot-history',
        label: 'Shot History',
        value: MachineSettingsSection.ShotHistory,
        prefixIcon: <ShotHistoryIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.shotHistory,
      },
      {
        id: 'live-fires',
        label: 'Live Fires',
        value: MachineSettingsSection.LiveFires,
        prefixIcon: <BarChartIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.liveFires,
      },
      {
        id: 'session-history',
        label: t('common.session-history'),
        value: MachineSettingsSection.SessionHistory,
        prefixIcon: <CounterClockwiseClockIcon />,
      },
      {
        id: 'burn-in-tests',
        label: 'Burn-In Tests',
        value: MachineSettingsSection.BurnInTests,
        prefixIcon: <StopwatchIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible: !userRoleFeatureFlags.adminMachines.burnInTests,
      },
      {
        id: 'actions',
        label: t('common.actions'),
        value: MachineSettingsSection.Actions,
        prefixIcon: <BadgeIcon />,
      },
    ];

    return ALL_MACHINE_SETTINGS_SECTIONS.filter(({ invisible }) => !invisible);
  }, [userRoleFeatureFlags]);

  const changeSection = (section: MachineSettingsSection) => {
    setActiveSection(section);

    // make sure we show the top of the section when we change
    const el = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);
    el?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // redirect to Status section if the user tries to access a section they're not allowed to see
  useEffect(() => {
    if (!visibleSections.find((s) => s.value === activeSection)) {
      setActiveSection(MachineSettingsSection.Status);
    }
  }, [activeSection, visibleSections]);

  const dialogTitle = machine
    ? MachineContextHelper.getMachineMenuLabel(machine)
    : '';

  const showSkeleton = !machine;

  // start/stop listening to fwd msgs for this machine
  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    const startData: IForwardWsRequest = {
      enable: true,
      machineID: machine.machineID,
    };

    const startMsg: IWSMsg = {
      type: WsMsgType.U2S_ForwardWs,
      data: startData,
    };

    getSocket()?.emit('message', startMsg);

    return () => {
      const stopData: IForwardWsRequest = {
        enable: false,
        machineID: machine.machineID,
      };

      const stopMsg: IWSMsg = {
        type: WsMsgType.U2S_ForwardWs,
        data: stopData,
      };

      getSocket()?.emit('message', stopMsg);
    };
  }, [machine?.machineID]);

  const dialogProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    title: showSkeleton ? (
      <Skeleton>
        {/* match the approx. width of the nav sidebar */}
        <Box width="230px">{dialogTitle}</Box>
      </Skeleton>
    ) : (
      dialogTitle
    ),
    width: RADIX.DIALOG.WIDTH.XXL,
    vFlexHeight: RADIX.DIALOG.HEIGHT.XL,
    /**
     * Slightly larger maxWidth allows navigation to fit on iPad.
     * Should we just make this the default?
     */
    maxWidth: '85vw',
    onOpenChange: (open) => {
      if (open) {
        return;
      }
      onClose();
    },
    onClose: onClose,
    modal: true,
    content: (
      <Grid
        columns={{
          xs: '7',
          lg: '5',
        }}
        className={RADIX.VFLEX.WRAPPER}
        gap={RADIX.FLEX.GAP.LG}
      >
        <Skeleton loading={showSkeleton}>
          <Box
            gridColumn={{
              xs: 'span 2',
              lg: 'span 1',
            }}
            height="100%"
            overflowY="auto"
            style={{
              scrollbarWidth: 'none',
            }}
          >
            <CommonNavMenu orientation="vertical">
              {visibleSections.map((s) => (
                <NavigationMenu.Item key={s.id}>
                  <NavigationMenu.Trigger
                    className={StringHelper.classNames([
                      'NavigationMenuTrigger',
                      activeSection === s.value ? 'active' : 'inactive',
                    ])}
                    onClick={() => changeSection(s.value)}
                  >
                    <Flex
                      pt="2"
                      pb="2"
                      pl="3"
                      pr="3"
                      gap={RADIX.FLEX.GAP.SM}
                      align="center"
                      overflow="hidden"
                    >
                      <Flex flexShrink="0">{s.prefixIcon}</Flex>
                      <Text
                        size="2"
                        truncate
                        color={
                          activeSection === s.value
                            ? RADIX.COLOR.ACCENT
                            : undefined
                        }
                      >
                        {t(s.label)}
                      </Text>
                      {s.suffixIcon && (
                        <Flex ml="auto" flexShrink="0">
                          {s.suffixIcon}
                        </Flex>
                      )}
                    </Flex>
                  </NavigationMenu.Trigger>
                </NavigationMenu.Item>
              ))}
            </CommonNavMenu>
          </Box>
        </Skeleton>
        <Skeleton loading={showSkeleton} height="100%">
          <Flex
            gridColumn={{
              xs: 'span 5',
              lg: 'span 4',
            }}
            overflowX="hidden"
            className={RADIX.VFLEX.COLUMN}
            gap={RADIX.FLEX.GAP.MD}
            pr="2"
            id={MACHINE_SETTINGS_CONTENT_ID}
          >
            <SectionContent />
          </Flex>
        </Skeleton>
      </Grid>
    ),
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...dialogProps} />
    </ErrorBoundary>
  );
};
